import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { bindAll } from '@bibliocommons/utils-react';
import { AlertFailure } from '@bibliocommons/deprecated-base-alerts';
import { Spinner } from '@bibliocommons/deprecated-base-icons';
import JacketCover from '@bibliocommons/shared-jacket-cover';
import { PrimaryButton } from '@bibliocommons/deprecated-base-buttons';

import listItemShape from 'app/shapes/oldLists/listItemShape';
import ItemAnnotationInput from '../../item-annotation-input';
import URLInput from '../../url-input';
import WebItemTitleInput from '../../web-item-title-input';

import '../styles/WebResults.scss';

export class WebResults extends React.PureComponent {
  constructor(props) {
    super(props);

    bindAll(this);
  }

  static handleFormSubmit(e) {
    e.preventDefault();
  }

  handleAddBib(item) {
    this.props.handleAddBib(item);
  }

  handleAnnotationChange(value) {
    this.props.handleWebItemPropertyChange(Immutable.Map({ annotation: value }));
  }

  handleAddWebItem() {
    if (this.webItemIsValid()) {
      this.props.handleAddWebItem();
    }
  }

  webItemIsValid() {
    const { webItem } = this.props;
    return webItem.get('url') && webItem.getIn(['jacketCover', 'localUrl']) && !webItem.get('invalidURL');
  }

  webItemIsAlreadyOnList() {
    const { webItem, listItems } = this.props;
    const existingItemIndex = listItems.findIndex(
      item => item.getIn(['urlListItem', 'url', 'url']) === webItem.get('url')
    );

    return existingItemIndex !== -1;
  }

  renderAddButton() {
    const { webItem } = this.props;
    return (
      <PrimaryButton
        className="add-url"
        disabled={!this.webItemIsValid() || !webItem.get('title') || this.webItemIsAlreadyOnList()}
        data-test-id="add-web-item-button"
        handleClick={this.handleAddWebItem}
      >
        <FormattedMessage id="add" />
      </PrimaryButton>
    );
  }

  renderPreview() {
    const { webItem } = this.props;

    // Check if URL is valid
    if (webItem.get('invalidURL')) {
      return (
        <AlertFailure>
          <span data-test-id="web-item-error">
            <FormattedMessage id="user_list_error_invalid_url" />
          </span>
        </AlertFailure>
      );
    }

    // Check if URL is already on list
    if (webItem.get('validated') && this.webItemIsAlreadyOnList()) {
      return (
        <AlertFailure>
          <FormattedMessage id="user_list_error_url_already_on_list" />
        </AlertFailure>
      );
    }

    // Display spinner if still waiting for previewURL
    if (webItem.get('validating')) {
      return <Spinner className="image-spinner" />;
    }

    // Show preview
    if (webItem.get('url') && webItem.getIn(['jacketCover', 'localUrl'])) {
      return (
        <div className="preview row">
          <div className="pull-left">
            <JacketCover format="WEBSITE" src={webItem.getIn(['jacketCover', 'localUrl'])} />
          </div>

          <div className="item-details">
            <span className="info-field website-title">
              <WebItemTitleInput
                value={webItem.get('title')}
                handleWebItemPropertyChange={this.props.handleWebItemPropertyChange}
              />
            </span>
            <span className="info-field url">{webItem.get('url')}</span>

            <span className="info-field format">
              <i aria-hidden className="icon-export" />
              <FormattedMessage id="list_web_resource" />
            </span>

            <ItemAnnotationInput
              allowFreeText
              value={this.props.webItem.get('annotation')}
              handleChange={this.handleAnnotationChange}
            />
          </div>
        </div>
      );
    }

    return null;
  }

  render() {
    const { webItem } = this.props;

    return (
      <form
        action=""
        className="cp-add-web-item"
        data-test-id="add-web-item-form"
        onSubmit={WebResults.handleFormSubmit}
        data-test-autopopulated={webItem.get('fieldsAutoPopulated') ? 'true' : 'false'}
      >
        <div className="url-field">
          <URLInput
            value={webItem.get('url')}
            previewURL={webItem.getIn(['jacketCover', 'localUrl'])}
            invalidURL={webItem.get('invalidURL')}
            handleWebItemPropertyChange={this.props.handleWebItemPropertyChange}
          />
          <PrimaryButton
            data-test-id="web-item-submit-url-button"
            className="submit-url-button"
            handleClick={this.props.handleWebURLSelected}
          >
            <FormattedMessage id="button_okay" />
          </PrimaryButton>
        </div>
        {this.renderPreview()}

        <div className="buttons row">{this.renderAddButton()}</div>
      </form>
    );
  }
}

WebResults.propTypes = {
  listItems: ImmutablePropTypes.listOf(listItemShape).isRequired,
  webItem: ImmutablePropTypes.contains({
    type: PropTypes.string,
    title: PropTypes.string,
    annotation: PropTypes.string,
    url: PropTypes.string,
    jacketCover: ImmutablePropTypes.contains({ localUrl: PropTypes.string })
  }),

  handleAddWebItem: PropTypes.func.isRequired,
  handleWebItemPropertyChange: PropTypes.func.isRequired,
  handleWebURLSelected: PropTypes.func.isRequired,
  handleAddBib: PropTypes.func
};

export default WebResults;
